import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckFast, faShieldHalved, faChartLine, faLeaf } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../i18n/LanguageContext';

const MetricsContainer = styled.section`
  padding: 120px 20px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.88) 80%, rgba(15, 23, 42, 0.82) 100%);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 90px 18px;
  }

  @media (max-width: 480px) {
    padding: 72px 16px;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top left, rgba(88, 156, 237, 0.32), transparent 45%),
    radial-gradient(circle at bottom right, rgba(0, 168, 232, 0.24), transparent 40%);
  opacity: 0.85;
`;

const ContentWrapper = styled.div`
  position: relative;
  max-width: 1180px;
  margin: 0 auto;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 50px;

  @media (max-width: 768px) {
    gap: 42px;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 780px;
  margin: 0 auto;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 768px) {
    gap: 14px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 3vw, 3rem);
`;

const SectionIntro = styled.p`
  margin: 0;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.72);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 26px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const MetricCard = styled.div`
  padding: 34px 28px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(18px);
  color: #fff;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 600px) {
    padding: 28px 24px;
    gap: 14px;
  }
`;

const MetricIcon = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(12, 77, 162, 0.22);
  color: var(--accent);
  font-size: 26px;
`;

const MetricValue = styled.div`
  font-size: clamp(2rem, 3vw, 2.8rem);
  font-weight: 700;
`;

const MetricLabel = styled.h3`
  margin: 0;
  font-size: 1.1rem;
`;

const MetricDescription = styled.p`
  margin: 0;
  font-size: 0.96rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.78);
`;

const Timeline = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 22px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const MilestoneCard = styled.div`
  position: relative;
  padding: 26px 24px 26px 32px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(15, 23, 42, 0.6);
  color: #fff;
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:before {
    content: '';
    position: absolute;
    left: 18px;
    top: 28px;
    bottom: 26px;
    width: 2px;
    background: linear-gradient(180deg, rgba(98, 164, 255, 0.12), rgba(98, 164, 255, 0.45));
  }

  @media (max-width: 600px) {
    padding: 24px 22px 24px 30px;
  }
`;

const MilestoneYear = styled.span`
  font-size: 0.85rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
`;

const MilestoneTitle = styled.h4`
  margin: 0;
  font-size: 1.15rem;
`;

const MilestoneDescription = styled.p`
  margin: 0;
  font-size: 0.94rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
`;

const MetricsSection = () => {
  const { t } = useLanguage();
  const metrics = t('home.metrics.stats');
  const milestones = t('home.metrics.milestones');
  const metricIcons = [faTruckFast, faShieldHalved, faChartLine, faLeaf];

  return (
    <MetricsContainer>
      <BackgroundGlow />
      <ContentWrapper>
        <Header>
          <SectionTitle>{t('home.metrics.title')}</SectionTitle>
          <SectionIntro>{t('home.metrics.intro')}</SectionIntro>
        </Header>
        <MetricsGrid>
          {metrics.map((metric, index) => (
            <MetricCard key={metric.label}>
              <MetricIcon>
                <FontAwesomeIcon icon={metricIcons[index]} />
              </MetricIcon>
              <MetricValue>{metric.value}</MetricValue>
              <MetricLabel>{metric.label}</MetricLabel>
              <MetricDescription>{metric.description}</MetricDescription>
            </MetricCard>
          ))}
        </MetricsGrid>
        <Timeline>
          {milestones.map((milestone) => (
            <MilestoneCard key={milestone.title}>
              <MilestoneYear>{milestone.year}</MilestoneYear>
              <MilestoneTitle>{milestone.title}</MilestoneTitle>
              <MilestoneDescription>{milestone.description}</MilestoneDescription>
            </MilestoneCard>
          ))}
        </Timeline>
      </ContentWrapper>
    </MetricsContainer>
  );
};

export default MetricsSection;